import {
  BellAlert,
  Bookmarks,
  Buildings,
  ChartBar,
  CubeSolid,
  Heart,
  Receipt,
  ShoppingCart,
  User
} from "@medusajs/icons";
import { Logo } from "@/components/logo";
import { PII_MASK_CLASS } from "@/lib/analytics";
import { Avatar, Button, DropdownMenu } from "@medusajs/ui";
import { cn } from "@repo/design-system/utils";
import { Link } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { type ReactNode, useState } from "react";
import MinimalistDock, { type DockItem } from "./minimal-dock";
import { QuickReorderButton } from "./QuickReorderButton";

interface AppHeaderProps {
  cartCount?: number;
  cartTotal?: number;
  /**
   * What the buyer has saved on their orders, already formatted in the orders'
   * currency (the same figure and format as the dashboard's Savings stat). The
   * chip is left out while it is unknown rather than showing a made-up number.
   */
  savedLabel?: string;
  searchBar: ReactNode;
  /**
   * The phone-only search entry point, shown in the header row below `md` where
   * the full `searchBar` is hidden. A compact trigger (an icon), not the bar -
   * see `MobileSearchButton`. Injected like `searchBar` so this component stays
   * unaware of the search route.
   */
  mobileSearch?: ReactNode;
  rightActions?: ReactNode;
  userName: string;
  userAvatar?: string;
  salonName?: string | null;
  notificationCount?: number;
  /**
   * Opens the phone nav drawer. The trigger lives here rather than in a band of
   * its own above the header: below `md` the header already owns a row, and a
   * separate logo/hamburger strip cost ~40px of a phone screen to repeat what
   * this row can carry. See storefront#145 (P4).
   */
  onOpenNav?: () => void;
  /**
   * Opens the cart slide-over. Set only in the `md`-to-`lg` band, where the
   * cart has left the layout but the phone's sticky bar hasn't taken over; when
   * it is undefined the cart button stays a link to the full cart page.
   */
  onOpenCart?: () => void;
  /**
   * The buyer's favorites list id (`kind: "favorites"`), if one exists yet. The
   * heart opens that list directly; without one it opens the Saved Lists index.
   */
  favoritesListId?: string | null;
}

export function AppHeader({
  searchBar,
  mobileSearch,
  cartCount = 0,
  cartTotal = 0,
  savedLabel,
  userName,
  salonName,
  userAvatar = "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face",
  rightActions,
  notificationCount = 0,
  onOpenNav,
  onOpenCart,
  favoritesListId
}: AppHeaderProps) {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  // Quick Actions Dock Items
  const quickActionItems = [
    {
      id: "favorites",
      icon: <Heart className="" />,
      label: "Favorites",
      href: "/account/saved-lists",
      search: favoritesListId ? { list: favoritesListId } : undefined
    },
    {
      id: "deals",
      icon: <Receipt className="" />,
      label: "Deals",
      onClick: () => console.log("Deals clicked")
    }
  ] satisfies DockItem[];

  // Notifications & Cart Dock Items
  const notificationCartItems = [
    {
      id: "notifications",
      icon: (
        <div className="relative">
          <BellAlert className="" />
          {notificationCount > 0 && (
            <div className="absolute -right-2 -top-2 flex h-4 w-4 animate-pulse items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
              {notificationCount > 9 ? "9+" : notificationCount}
            </div>
          )}
        </div>
      ),
      label: "Notifications",
      href: "/account/messages"
    },
    {
      id: "cart",
      icon: (
        <div className="relative">
          <ShoppingCart className="" />
          {cartCount > 0 && (
            <div className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-purple-600 text-[9px] font-bold text-white">
              {cartCount > 9 ? "9+" : cartCount}
            </div>
          )}
        </div>
      ),
      label: `Cart • ${cartCount} items • $${cartTotal.toFixed(2)}`,
      // A link at the widths that have the rail or the sticky bar; the
      // slide-over's trigger in between. Exactly one of the two is set.
      ...(onOpenCart ? { onClick: onOpenCart } : { href: "/cart" })
    }
  ] satisfies DockItem[];

  return (
    <header className="relative z-40 flex-shrink-0 border-b border-gray-200 bg-white px-4 py-2 md:px-6 md:py-1">
      {/* One row at every width. Below `md` it carries the brand and nav
          trigger on the left and the action icons (search included, as an icon)
          on the right; the full search bar is hidden, since on a phone it is
          only ever a trigger for `/search/input` and a whole line spent on a box
          the buyer can't type suggestions into was the cramped-row cost in
          storefront#145 §6. From `md` the brand and the mobile search icon drop
          out, and the full search bar takes the middle as it always did. */}
      <div className="flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-2">
        {/* Phone-only: nav trigger + brand. The nav rail carries both from md. */}
        <div className="flex items-center gap-1 md:hidden">
          {onOpenNav && (
            <button
              type="button"
              onClick={onOpenNav}
              aria-label="Open menu"
              className="-ml-2 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-neutral-800 transition-colors hover:bg-gray-50 dark:text-neutral-200"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
          <Link to="/" className="flex items-center" aria-label="Home">
            <Logo variant="mobile" />
          </Link>
        </div>

        {/* Middle Section - full Search Bar, from `md` up. */}
        {/* `min-w-0` is load-bearing, not tidying: this is a flex item, so it
            defaults to `min-width: auto` and refuses to shrink below its
            content's min-content width. The shell above is `overflow-hidden`,
            so a column that won't shrink doesn't produce a scrollbar - it
            pushes whatever shares its line off-screen. It shares the row with
            the action dock from `md` up, so keep it. Below `md` the bar is
            hidden and the phone search icon in the right cluster stands in. */}
        <div className="hidden min-w-0 items-center md:order-none md:flex md:w-auto md:max-w-xl md:flex-1">
          {searchBar}
        </div>

        {/* Right Section - User Actions. Tight on a phone (`gap-0.5`) so the
            search, bell, cart and avatar read as one cluster instead of drifting
            apart across a 360px row; back to `gap-2` from `md`. */}
        <div className="flex items-center gap-0.5 md:order-last md:gap-2">
          {/* Phone-only search icon; the full search bar returns from `md`. */}
          {mobileSearch && <div className="md:hidden">{mobileSearch}</div>}

          {/* Savings Display - More Compact */}
          {savedLabel && (
            <div className="hidden items-center rounded-md border border-green-200 bg-green-50 px-2.5 py-1.5 xl:flex">
              <ChartBar className="mr-1.5 h-3.5 w-3.5 text-green-600" />
              <span className="text-[11px] font-medium text-green-700">
                {savedLabel} saved
              </span>
            </div>
          )}

          {/* Quick Actions - Minimalist Dock */}
          <div className="hidden items-center lg:flex">
            <QuickReorderButton />
            <MinimalistDock
              items={quickActionItems}
              variant="header"
              className="p-0"
            />
          </div>

          {/* Notifications & Cart - Minimalist Dock */}
          <div className="flex items-center">
            <MinimalistDock
              items={notificationCartItems}
              variant="header"
              className="p-0"
            />
          </div>

          {/* User Profile Dropdown - Simplified */}
          <DropdownMenu
            open={isProfileMenuOpen}
            onOpenChange={setIsProfileMenuOpen}
          >
            <DropdownMenu.Trigger asChild>
              <Button
                variant="transparent"
                className="cursor-pointer rounded-lg p-1.5 transition-colors duration-200 hover:bg-gray-50"
              >
                <Avatar
                  src={userAvatar}
                  fallback={userName.charAt(0)}
                  size="small"
                  className="ring-2 ring-gray-100"
                />
              </Button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Content
              align="end"
              className="z-50 mt-2 w-56 rounded-lg border border-gray-200 bg-white p-2 shadow-lg"
              sideOffset={4}
            >
              {/* The account header names the person and their salon - masked
                  out of session replays (ADR-0006). */}
              <DropdownMenu.Label
                className={cn(
                  "mb-2 border-b border-gray-100 px-3 py-2",
                  PII_MASK_CLASS
                )}
              >
                <div className="flex items-center space-x-3">
                  <Avatar
                    src={userAvatar}
                    fallback={userName.charAt(0)}
                    size="small"
                    className="ring-2 ring-purple-100"
                  />
                  <div>
                    <p className="text-xs font-semibold text-gray-900">
                      {userName}
                    </p>
                    <p className="text-[11px] text-gray-500">
                      {salonName || "No salon name"}
                    </p>
                  </div>
                </div>
              </DropdownMenu.Label>

              <DropdownMenu.Item className="rounded-md px-3 py-2 text-xs text-gray-700 transition-colors hover:bg-gray-50">
                <Link
                  to="/account"
                  className="flex cursor-pointer items-center space-x-3"
                >
                  <User className="h-5 w-5 text-gray-500" />
                  <span>Account Settings</span>
                </Link>
              </DropdownMenu.Item>

              <DropdownMenu.Item className="flex cursor-pointer items-center space-x-3 rounded-md px-3 py-2 text-xs text-gray-700 transition-colors hover:bg-gray-50">
                <Link
                  to="/orders"
                  className="flex cursor-pointer items-center space-x-3"
                >
                  <CubeSolid className="h-5 w-5 text-gray-500" />
                  <span>Order History</span>
                </Link>
              </DropdownMenu.Item>

              <DropdownMenu.Item className="flex cursor-pointer items-center space-x-3 rounded-md px-3 py-2 text-xs text-gray-700 transition-colors hover:bg-gray-50">
                <Link
                  to="/account/saved-lists"
                  className="flex cursor-pointer items-center space-x-3"
                >
                  <Bookmarks className="h-5 w-5 text-gray-500" />
                  <span>Saved Lists</span>
                </Link>
              </DropdownMenu.Item>

              <DropdownMenu.Item className="flex cursor-pointer items-center space-x-3 rounded-md px-3 py-2 text-xs text-gray-700 transition-colors hover:bg-gray-50">
                <Buildings className="h-5 w-5 text-gray-500" />
                <span>Salon Profile</span>
              </DropdownMenu.Item>

              <div className="my-2 border-t border-gray-100"></div>

              <DropdownMenu.Item className="flex cursor-pointer items-center space-x-3 rounded-md px-3 py-2 text-xs text-red-600 transition-colors hover:bg-red-50">
                <Link to="/logout" preload={false}>
                  Sign Out
                </Link>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu>

          {/* Custom Right Actions */}
          {rightActions}
        </div>
      </div>
    </header>
  );
}
